import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { InputNumber } from 'primereact/inputnumber';
import { Fieldset } from 'primereact/fieldset';
import { useParams } from "react-router-dom";
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { ErrorValidationMessage } from './ErrorValidationMessage';
import api from '../api/api';
import '../layout/sass/_form.scss';

export const FormPessoaMudas = ({
    isEditing,
    isLoading,
    setPessoaMudas,
    listMudas,
    setListMudas,
    rural,
    quantidadeTotal,
    setQuantidadeTotal
}) => {
    const { id: pessoaId } = useParams();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false);
    const [campanhaQuantidadeRural, setCampanhaQuantidadeRural] = useState(null);
    const [campanhaQuantidadeUrbano, setCampanhaQuantidadeUrbano] = useState(null);
    const [campanhaAtiva, setCampanhaAtiva] = useState(null);
    const [selectedMudaSaldo, setSelectedMudaSaldo] = useState(null);

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getLimiteMaximoZona = useCallback(() => {
        if (rural === true) return campanhaQuantidadeRural || 999;
        if (rural === false) return campanhaQuantidadeUrbano || 999;
        return 0;
    }, [rural, campanhaQuantidadeRural, campanhaQuantidadeUrbano]);

    const getLimiteDisponivelUsuario = useCallback(() => {
        const limiteZona = getLimiteMaximoZona();
        const restanteZona = limiteZona - (quantidadeTotal || 0);
        return Math.max(0, restanteZona);
    }, [getLimiteMaximoZona, quantidadeTotal]);

    const schema = Yup.object().shape({
        mudaId: Yup
            .number()
            .required("A muda é obrigatória")
            .moreThan(0, "Selecione uma muda válida"),
        quantidade: Yup
            .number()
            .required("A quantidade é obrigatória")
            .min(1, "A quantidade mínima é 1")
            .test(
                "valida-limite",
                "Quantidade acima do permitido",
                function (value) {
                    const limiteDisp = getLimiteDisponivelUsuario();
                    if (value > limiteDisp) {
                        return this.createError({
                            message: `Pode selecionar no máximo mais ${limiteDisp} muda(s).`
                        });
                    }
                    return true;
                }
            )
    });

    const formik = useFormik({
        initialValues: {
            mudaId: 0,
            quantidade: 1
        },
        validationSchema: schema,
        enableReinitialize: false,

        onSubmit: async (data) => {
            try {
                // 1. Validação estrita da Zona
                if (typeof rural !== 'boolean') {
                    toast.current.show({
                        life: 3000,
                        severity: 'error',
                        summary: 'Selecione a Zona (Urbana ou Rural) no formulário acima!'
                    });
                    return;
                }

                setIsSubmiting(true);

                // 2. Busca dados da campanha ativa e da muda da campanha
                const [tempData, campanhaRes] = await Promise.all([
                    api.get(`/campanhaMudas/campanhaAtiva/${data.mudaId}`),
                    api.get(`campanha/ativoCampanha`)
                ]);

                const campanhaId = campanhaRes.data.id;
                const campanhaMudaId = tempData.data.id;
                const qtdAdicionada = parseInt(data.quantidade, 10);
                const maxPermitidoCampanha = rural ? campanhaQuantidadeRural : campanhaQuantidadeUrbano;

                // 3. Validação em tempo real do Saldo no Back-end via endpoint dedicado
                const saldoRes = await api.get('/campanhaMudas/saldo', {
                    params: { campanhaId, campanhaMudaId }
                });
                const saldoAtualizado = Number(saldoRes.data);

                if (qtdAdicionada > saldoAtualizado) {
                    toast.current.show({
                        life: 4000,
                        severity: 'error',
                        summary: saldoAtualizado === 0
                            ? "Muda selecionada não possui saldo disponível!"
                            : `Estoque insuficiente no servidor! Saldo atual: ${saldoAtualizado}`
                    });
                    return;
                }

                // 4. Validação do Limite Acumulado da Zona
                if ((quantidadeTotal + qtdAdicionada) > maxPermitidoCampanha) {
                    toast.current.show({
                        life: 4000,
                        severity: 'error',
                        summary: `Limite excedido! O máximo é ${maxPermitidoCampanha} mudas para a zona ${rural ? 'rural' : 'urbana'}.`
                    });
                    return;
                }

                // 5. Montagem do objeto formatado para exibição na lista
                const mudaSelecionadaObj = listMudas?.find(m => m.id === data.mudaId);

                const novaMudaFormatada = {
                    quantidade: qtdAdicionada,
                    campanhaMudaId: campanhaMudaId,
                    campanhaId: campanhaId,
                    campanhaMuda: {
                        id: campanhaMudaId,
                        campanhaId: campanhaId,
                        muda: mudaSelecionadaObj || tempData.data.muda
                    }
                };

                // 6. Atualização dos estados locais
                setPessoaMudas(prev => [...prev, novaMudaFormatada]);

                if (typeof setQuantidadeTotal === 'function') {
                    setQuantidadeTotal(prev => prev + qtdAdicionada);
                }

                if (listMudas && typeof setListMudas === 'function') {
                    setListMudas(prevList => prevList.filter(muda => muda.id !== data.mudaId));
                }

                formik.resetForm({
                    values: {
                        mudaId: 0,
                        quantidade: 1
                    }
                });

                setSelectedMudaSaldo(null);
                toast.current.show({ life: 2000, severity: 'success', summary: 'Muda adicionada à lista!' });

            } catch (error) {
                console.error("Erro ao adicionar muda:", error);
                const mensagemErro = error.response?.data || 'Erro ao consultar saldo ou adicionar a muda!';
                toast.current.show({ life: 4000, severity: 'error', summary: mensagemErro });
            } finally {
                setIsSubmiting(false);
            }
        }
    });

    async function getCampanhaQuantidades() {
        try {
            const response = await api.get(`campanha/ativoCampanha`);
            setCampanhaAtiva(response.data);
            setCampanhaQuantidadeRural(response.data.qtde_mudas_rural);
            setCampanhaQuantidadeUrbano(response.data.qtde_mudas_urbana);
        } catch (e) {
            console.error("Erro ao carregar quantidades da campanha:", e);
        }
    }

    // Consulta de saldo atualizada apontando para o endpoint /campanhaMudas/saldo
    const getSaldoMuda = useCallback(async () => {
        if (formik.values.mudaId && formik.values.mudaId > 0 && campanhaAtiva?.id) {
            try {
                const tempData = await api.get(`/campanhaMudas/campanhaAtiva/${formik.values.mudaId}`);
                if (tempData.data?.id) {
                    const response = await api.get('/campanhaMudas/saldo', {
                        params: {
                            campanhaId: campanhaAtiva.id,
                            campanhaMudaId: tempData.data.id
                        }
                    });
                    setSelectedMudaSaldo(response.data);
                }
            } catch (e) {
                console.error("Erro ao buscar saldo da muda:", e);
                setSelectedMudaSaldo(null);
            }
        } else {
            setSelectedMudaSaldo(null);
        }
    }, [formik.values.mudaId, campanhaAtiva]);

    useEffect(() => {
        getCampanhaQuantidades();
    }, []);

    useEffect(() => {
        getSaldoMuda();
    }, [getSaldoMuda]);

    const handleAdicionarMudaClick = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        formik.handleSubmit();
    };

    return (
        <Fieldset legend="Mudas" toggleable>
            <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
                <Toast ref={toast} position="bottom-right" />
                <div className="p-col-12">
                    <div className="p-formgrid p-grid p-fluid p-mx-0">

                        <div className="p-field p-col-12 p-sm-5">
                            <label
                                htmlFor="mudaId"
                                className={classNames({ 'p-error': isFormFieldValid('mudaId') })}
                            >
                                Mudas {selectedMudaSaldo !== null && `(Saldo: ${selectedMudaSaldo})`}
                            </label>
                            {!isLoading ? (
                                <Dropdown
                                    id="mudaId"
                                    name="mudaId"
                                    options={listMudas || []}
                                    filter={true}
                                    emptyMessage={"Nenhuma muda encontrada"}
                                    optionLabel="nome"
                                    optionValue="id"
                                    placeholder="Selecione uma muda"
                                    className={classNames({ 'p-invalid': isFormFieldValid('mudaId') })}
                                    value={formik.values.mudaId}
                                    onChange={option => formik.setFieldValue("mudaId", option.value)}
                                    emptyFilterMessage={"Nenhuma muda encontrada"}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.mudaId && formik.touched.mudaId &&
                                <ErrorValidationMessage message={formik.errors.mudaId} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-3">
                            <label
                                htmlFor="quantidade"
                                className={classNames({ 'p-error': isFormFieldValid('quantidade') })}
                            >
                                Quantidade (Máx: {getLimiteDisponivelUsuario()})
                            </label>
                            {!isLoading ? (
                                <InputNumber
                                    id="quantidade"
                                    name="quantidade"
                                    value={formik.values.quantidade}
                                    onValueChange={(e) => formik.setFieldValue("quantidade", e.value || 1)}
                                    min={1}
                                    max={getLimiteDisponivelUsuario() || 1}
                                    showButtons
                                    className={classNames({ 'p-invalid': isFormFieldValid('quantidade') })}
                                />
                            ) : (
                                <Skeleton height="35px" />
                            )}
                            {formik.errors.quantidade && formik.touched.quantidade &&
                                <ErrorValidationMessage message={formik.errors.quantidade} />
                            }
                        </div>

                        <div className="p-field p-col-12 p-sm-4 p-as-end">
                            <Button
                                type="button"
                                icon="pi pi-plus"
                                label="Adicionar Muda"
                                className="p-button-primary"
                                onClick={handleAdicionarMudaClick}
                                loading={isSubmiting}
                            />
                        </div>

                    </div>
                </div>
            </div>
        </Fieldset>
    );
};